/**
 * SortingWorkflowPage — `/mail/sorting`.
 *
 * Ported from `screens/src/pages/mail/SortingWorkflow.jsx`. Lets a sorter
 * scan a tracking number and push the item along its sort-bin classification
 * + state-machine transition.
 *
 * Backend wiring:
 *   - GET /v1/mail-items/:id/sort-bin → classification.
 *   - POST /v1/mail-items/:id/transitions  with event=SORT or DISPATCH.
 *
 * The screens prototype renders an aggregate "items by destination zone"
 * dashboard. That depends on a service-reporting-ml endpoint that doesn't
 * ship today, so the aggregate panel renders a deferred notice and the
 * actionable scan-and-classify path is fully live.
 */
import { type FormEvent, type JSX, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Package, Send } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  getSortingBoard,
  listMailItems,
  transitionMailItem,
  type SortBoardBin,
} from '../../lib/api/mail-items';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';

export function SortingWorkflowPage(): JSX.Element {
  const [trackingNumber, setTrackingNumber] = useState<string>('');
  const [submitted, setSubmitted] = useState<string>('');
  const queryClient = useQueryClient();

  const lookup = useQuery({
    queryKey: ['mail', 'sorting', 'lookup', submitted],
    queryFn: () => listMailItems({ trackingNumber: submitted, pageSize: 1 }),
    enabled: submitted.length > 0,
    retry: false,
  });

  const board = useQuery({
    queryKey: ['mail', 'sorting', 'board'],
    queryFn: () => getSortingBoard(),
    retry: false,
    staleTime: 15_000,
  });
  const boardDenied =
    board.isError && board.error instanceof ApiError && (board.error.status === 401 || board.error.status === 403);

  const item = lookup.data?.items[0] ?? null;

  const mutate = useMutation({
    mutationFn: (event: 'SORT' | 'DISPATCH') => {
      if (!item) throw new Error('No item');
      return transitionMailItem(item.id, { event });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mail', 'sorting'] });
      void queryClient.invalidateQueries({ queryKey: ['mail', 'items'] });
    },
  });

  function handleScan(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setSubmitted(trackingNumber.trim().toUpperCase());
  }

  const denied = lookup.isError && lookup.error instanceof ApiError && (lookup.error.status === 401 || lookup.error.status === 403);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Sorting Workflow"
        description="Scan an item to classify into a destination sort bin and dispatch."
      />

      <form onSubmit={handleScan} className="bg-white border border-border rounded-xl p-5">
        <label className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-2">
          <Package className="w-4 h-4 text-nipost-green" /> Scan tracking number
        </label>
        <div className="flex gap-3">
          <input
            type="text"
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
            placeholder="EE123456785NG"
            autoComplete="off"
            spellCheck={false}
            aria-label="Tracking number"
            className="flex-1 px-3 py-2.5 text-base font-mono uppercase border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green"
          />
          <button
            type="submit"
            className="px-6 py-2.5 bg-nipost-green text-white font-semibold rounded-lg hover:bg-nipost-green-dark"
          >
            Scan
          </button>
        </div>
      </form>

      {submitted.length === 0 ? (
        <p className="text-sm text-muted">Scan an item to begin.</p>
      ) : lookup.isPending ? (
        <p className="text-sm text-muted">Looking up…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          <code className="font-mono">Mail/classifySortBin</code> is missing from the dev token.
        </DeferredNotice>
      ) : !item ? (
        <div className="bg-white border border-border rounded-xl p-6 text-sm text-muted">
          No item found for <span className="font-mono">{submitted}</span>.
        </div>
      ) : (
        <div className="bg-white border border-border rounded-xl p-5 space-y-3">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-semibold text-muted uppercase tracking-widest">Item</p>
              <p className="font-mono text-base font-bold text-gray-900">{item.trackingNumber}</p>
              <p className="text-xs text-muted mt-1">Service {item.serviceClass}</p>
            </div>
            <StatusBadge status={item.status} />
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border">
            <button
              type="button"
              disabled={mutate.isPending}
              onClick={() => mutate.mutate('SORT')}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-60"
            >
              <Package className="w-4 h-4" /> Mark sorted
            </button>
            <button
              type="button"
              disabled={mutate.isPending}
              onClick={() => mutate.mutate('DISPATCH')}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-60"
            >
              <Send className="w-4 h-4" /> Dispatch
            </button>
            {mutate.isError ? (
              <span className="text-xs text-red-700">{(mutate.error as Error).message}</span>
            ) : null}
            {mutate.isSuccess ? (
              <span className="text-xs text-nipost-green">Transition recorded.</span>
            ) : null}
          </div>
        </div>
      )}

      <section data-testid="sorting-board" className="space-y-3">
        <div className="flex items-baseline justify-between">
          <h2 className="text-base font-semibold text-gray-900">Sort-bin board</h2>
          <p className="text-xs text-muted">Mail items grouped by destination bin</p>
        </div>
        {board.isPending ? (
          <p className="text-sm text-muted">Loading board…</p>
        ) : boardDenied ? (
          <DeferredNotice title="Permission required">
            <code className="font-mono">Mail/readMailItem</code> is missing from the dev token.
          </DeferredNotice>
        ) : board.isError ? (
          <DeferredNotice title="Service unreachable">
            {(board.error as Error).message}
          </DeferredNotice>
        ) : (board.data?.bins.length ?? 0) === 0 ? (
          <div className="bg-white border border-border rounded-xl p-6 text-sm text-muted text-center">
            No in-flight items currently classified.{' '}
            <span className="text-gray-700">{board.data?.totalUnsorted ?? 0}</span> unsorted.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <BoardSummary label="In-flight" value={board.data?.totalItems ?? 0} />
              <BoardSummary label="Bins active" value={board.data?.bins.length ?? 0} />
              <BoardSummary label="Unsorted" value={board.data?.totalUnsorted ?? 0} tone={board.data?.totalUnsorted ? 'warn' : 'default'} />
              <BoardSummary label="Sorted" value={(board.data?.totalItems ?? 0) - (board.data?.totalUnsorted ?? 0)} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {(board.data?.bins ?? []).map((bin) => (
                <BoardBinCard key={bin.binCode} bin={bin} />
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function BoardSummary({ label, value, tone = 'default' }: { label: string; value: number; tone?: 'default' | 'warn' }): JSX.Element {
  const colour = tone === 'warn' && value > 0 ? 'text-amber-700' : 'text-gray-900';
  return (
    <div className="bg-white border border-border rounded-lg px-3 py-2">
      <p className="text-[10px] uppercase tracking-widest text-muted">{label}</p>
      <p className={`mt-0.5 text-lg font-semibold ${colour}`}>{value}</p>
    </div>
  );
}

function BoardBinCard({ bin }: { bin: SortBoardBin }): JSX.Element {
  return (
    <div className="bg-white border border-border rounded-xl p-4" data-testid={`bin-${bin.binCode}`}>
      <div className="flex items-baseline justify-between">
        <p className="font-mono text-sm font-bold text-gray-900">{bin.binCode}</p>
        <span className="text-[10px] uppercase tracking-widest text-muted">{bin.laneClass}</span>
      </div>
      <p className="text-[11px] text-muted">→ {bin.nextHubCode}</p>
      <p className="mt-2 text-2xl font-semibold text-gray-900">{bin.total}</p>
      <ul className="mt-2 flex flex-wrap gap-1.5">
        {Object.entries(bin.byStatus).map(([status, count]) => (
          <li key={status} className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 border border-border text-gray-700 font-mono">
            {status}: {count}
          </li>
        ))}
      </ul>
    </div>
  );
}
